import type { ReactNode } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { Button as TamaguiButton, GetProps, XStack, YStack, styled } from 'tamagui';

import { AppHeading } from './primitives';

const styledUnsafe = styled as any;
const UnsafeButton = TamaguiButton as any;
const UnsafeXStack = XStack as any;
const UnsafeYStack = YStack as any;

const isAppleNativeShell = Platform.OS === 'ios';
const APPLE_TABLET_MIN_WIDTH = 820;

export const AppShell = styledUnsafe(YStack, {
  name: 'AppShell',
  flex: 1,
  width: '100%',
  backgroundColor: '$canvas',
  position: 'relative',
});

export const AppShellMain = styledUnsafe(YStack, {
  name: 'AppShellMain',
  flex: 1,
  width: '100%',
});

export const AppContentContainer = styledUnsafe(YStack, {
  name: 'AppContentContainer',
  width: '100%',
  maxWidth: 1152,
  alignSelf: 'center',
  px: '$sm',
  $gtSm: {
    px: '$lg',
  },
});

type AppHeroShellProps = {
  title: string;
  leading?: ReactNode;
  action?: ReactNode;
  backAction?: ReactNode;
  titleLines?: number;
  onTitlePress?: () => void;
  titleActionLabel?: string;
};

export function AppHeroShell({
  title,
  leading,
  action,
  backAction,
  titleLines = 1,
  onTitlePress,
  titleActionLabel,
}: AppHeroShellProps) {
  const { width } = useWindowDimensions();
  const isAppleTabletShell = isAppleNativeShell && width >= APPLE_TABLET_MIN_WIDTH;
  const heroContent = (
    <UnsafeXStack width="100%" alignItems="center" gap={isAppleTabletShell ? '$md' : '$sm'}>
      {backAction ? (
        <UnsafeXStack alignItems="center" justifyContent="center" flexShrink={0}>
          {backAction}
        </UnsafeXStack>
      ) : null}
      {leading ? (
        <UnsafeXStack alignItems="center" justifyContent="center" flexShrink={0}>
          {leading}
        </UnsafeXStack>
      ) : null}
      {onTitlePress ? (
        <UnsafeButton
          unstyled
          flex={1}
          minWidth={0}
          alignItems="flex-start"
          justifyContent="center"
          pressStyle={{ opacity: 0.82 }}
          onPress={onTitlePress}
          accessibilityRole="button"
          accessibilityLabel={titleActionLabel ?? title}
        >
          <AppHeading
            accessibilityRole="header"
            width="100%"
            numberOfLines={titleLines}
            fontSize={isAppleNativeShell ? (isAppleTabletShell ? '$xxl' : '$lg') : '$lg'}
            lineHeight={isAppleNativeShell ? (isAppleTabletShell ? '$xxl' : '$lg') : '$lg'}
            letterSpacing={isAppleNativeShell ? (isAppleTabletShell ? '$lg' : '$md') : '$md'}
            $gtSm={{ fontSize: '$xl', lineHeight: '$xl', letterSpacing: '$lg' }}
          >
            {title}
          </AppHeading>
        </UnsafeButton>
      ) : isAppleNativeShell ? (
        <AppHeading
          accessibilityRole="header"
          flex={1}
          minWidth={0}
          numberOfLines={titleLines}
          fontSize={isAppleTabletShell ? '$xxl' : '$lg'}
          lineHeight={isAppleTabletShell ? '$xxl' : '$lg'}
          letterSpacing={isAppleTabletShell ? '$lg' : '$md'}
          $gtSm={{ fontSize: '$xl', lineHeight: '$xl', letterSpacing: '$lg' }}
        >
          {title}
        </AppHeading>
      ) : (
        <AppHeading
          accessibilityRole="header"
          flex={1}
          minWidth={0}
          numberOfLines={titleLines}
          fontSize="$lg"
          lineHeight="$lg"
          letterSpacing="$md"
          $gtSm={{ fontSize: '$xl', lineHeight: '$xl', letterSpacing: '$lg' }}
        >
          {title}
        </AppHeading>
      )}
      {action ? (
        <UnsafeXStack alignItems="center" justifyContent="center" flexShrink={0}>
          {action}
        </UnsafeXStack>
      ) : null}
    </UnsafeXStack>
  );

  if (isAppleNativeShell) {
    return (
      <UnsafeYStack
        backgroundColor="$panelBackground"
        borderWidth={1}
        borderColor="$borderColor"
        overflow="hidden"
        shadowColor="$shadowColor"
        shadowOffset={{ width: 0, height: 8 }}
        shadowOpacity={0.06}
        shadowRadius={18}
        elevation={1}
        px={isAppleTabletShell ? 18 : 14}
        py={isAppleTabletShell ? 6 : 4}
        minHeight={isAppleTabletShell ? 70 : 60}
        justifyContent="center"
        borderRadius={isAppleTabletShell ? 22 : 20}
        $gtSm={{ px: isAppleTabletShell ? 20 : 16, py: isAppleTabletShell ? 8 : 6, minHeight: isAppleTabletShell ? 72 : 64 }}
      >
        {heroContent}
      </UnsafeYStack>
    );
  }

  return (
    <UnsafeYStack
      backgroundColor="$panelBackground"
      borderWidth={1}
      borderColor="$borderColor"
      borderRadius="$md"
      overflow="hidden"
      shadowColor="$shadowColor"
      shadowOffset={{ width: 0, height: 10 }}
      shadowOpacity={0.05}
      shadowRadius={18}
      elevation={1}
      px="$md"
      py="$xs"
      minHeight={58}
      $gtSm={{ px: '$lg', py: '$xs', minHeight: 60 }}
    >
      {heroContent}
    </UnsafeYStack>
  );
}

export const AppTabBar = styledUnsafe(XStack, {
  name: 'AppTabBar',
  width: '100%',
  alignItems: 'center',
  gap: 4,
  backgroundColor: '$tabBarBackground',
  borderWidth: 1,
  borderColor: '$borderColor',
  borderRadius: '$pill',
  p: '$xs',
  shadowColor: '$shadowStrongColor',
  shadowOffset: { width: 0, height: 12 },
  shadowOpacity: 0.1,
  shadowRadius: 20,
  elevation: 6,
});

type AppTabButtonProps = Omit<GetProps<typeof TamaguiButton>, 'children'> & {
  active?: boolean;
  children: ReactNode;
};

export function AppTabButton({
  active = false,
  children,
  accessibilityState,
  accessibilityRole = 'tab',
  ...props
}: AppTabButtonProps) {
  return (
    <UnsafeButton
      unstyled
      accessibilityRole={accessibilityRole}
      accessibilityState={{
        ...accessibilityState,
        selected: active || Boolean(accessibilityState?.selected),
      }}
      flexBasis={0}
      flexGrow={1}
      flexShrink={1}
      minWidth={0}
      px="$xs"
      py="$xs"
      borderRadius="$pill"
      alignItems="center"
      justifyContent="center"
      pressStyle={{ opacity: 0.92 }}
      data-active={active ? 'true' : 'false'}
      {...props}
    >
      {children}
    </UnsafeButton>
  );
}
